import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  maxTextLength?: number;
  className?: string;
  testId?: string;
}

const TOOLBAR = [
  ['bold', 'italic'],
  [{ list: 'bullet' }, { list: 'ordered' }],
  ['clean'],
];

/** Normalise Quill's empty-document representation to an empty string. */
function normaliseEmpty(html: string): string {
  return html === '<p><br></p>' || html === '<p></p>' ? '' : html;
}

/** Replace &nbsp; entities Quill emits with regular spaces so stored HTML stays clean. */
function normaliseSpaces(html: string): string {
  return html.replace(/&nbsp;/g, ' ');
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Write something…',
  maxTextLength,
  className = '',
  testId,
}: RichTextEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  // Track the last HTML we pushed INTO Quill so the change handler can
  // skip echoing it back (avoids cursor-jump loops).
  const lastSetHtml = useRef<string>('');
  // Set to true during programmatic pastes to suppress text-change callbacks.
  const isProgrammatic = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const quill = new Quill(containerRef.current, {
      theme: 'snow',
      placeholder,
      modules: { toolbar: TOOLBAR },
    });

    quillRef.current = quill;

    // Expose a stable testId on the contenteditable element for Playwright.
    if (testId) {
      const editorEl = containerRef.current?.querySelector<HTMLElement>('.ql-editor');
      if (editorEl) editorEl.dataset.testid = testId;
    }

    // Seed with the initial value (may be empty). text-change isn't
    // registered yet so no need for the isProgrammatic guard here.
    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
      // Store Quill's actual output (may differ from raw value, e.g. wraps
      // plain text in <p> tags) so the handler baseline is accurate.
      lastSetHtml.current = normaliseSpaces(normaliseEmpty(quill.getSemanticHTML()));
      // Seeding leaves the editor focused. Nothing asked for focus on mount.
      quill.blur();
    }

    quill.on('text-change', () => {
      if (isProgrammatic.current) return;

      const html = normaliseSpaces(normaliseEmpty(quill.getSemanticHTML()));

      // Enforce text-only length limit without mutating Quill's state.
      if (maxTextLength !== undefined) {
        const textLen = quill.getText().trim().length;
        if (textLen > maxTextLength) return; // silently ignore the excess
      }

      if (html !== lastSetHtml.current) {
        onChange(html);
      }
    });

    return () => {
      // Quill v2 has no explicit destroy(); clear the DOM manually.
      quill.off('text-change');
      quillRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
    // Intentionally empty deps — Quill owns the DOM; we sync via the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes (e.g. when the profile loads after mount).
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;

    const current = normaliseSpaces(normaliseEmpty(quill.getSemanticHTML()));
    if (value === current) return; // already in sync
    if (value === lastSetHtml.current) return; // we set this ourselves

    // Suppress the synchronous text-change that dangerouslyPasteHTML fires.
    // Without this, Quill's normalised output (e.g. '<p>text</p>') would
    // differ from the raw stored value ('text'), calling onChange and making
    // the form appear dirty before the user touches anything.
    isProgrammatic.current = true;
    // Whether the user was actually in the field, not just where the caret was.
    // dangerouslyPasteHTML leaves the editor focused, and setSelection focuses
    // it too — so restoring the caret unconditionally stole focus the moment
    // the profile finished loading, which is why the cursor landed in the bio
    // on page load. Only put it back if it was there to begin with.
    const hadFocus = quill.hasFocus();
    const sel = hadFocus ? quill.getSelection() : null;
    quill.clipboard.dangerouslyPasteHTML(value ?? '');
    // Update baseline to Quill's actual output so future user edits compare correctly.
    lastSetHtml.current = normaliseSpaces(normaliseEmpty(quill.getSemanticHTML()));
    isProgrammatic.current = false;

    if (hadFocus && sel) quill.setSelection(sel);
    else quill.blur();
  }, [value]);

  return (
    <div className={`rich-text-editor ${className}`}>
      <div ref={containerRef} />
    </div>
  );
}
